import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api, subscribe, type Page, type Proposal, type PublicUser } from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, Skeleton, inputClass, relativeTime } from "../components/ui";

// "All" first and selected by default: opening the page should show everything
// that exists, not a filtered subset that looks like an empty database when the
// queue happens to be clear.
const FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Waiting" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

const TONE = {
  pending: "warn",
  approved: "ok",
  rejected: "danger",
} as const;

export function Proposals({ user }: { user: PublicUser }) {
  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<Proposal[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canDecide = user.role === "owner" || user.role === "admin" || user.role === "operator";

  const load = useCallback(
    async (append = false) => {
      const params = new URLSearchParams({ limit: "25" });
      if (status) params.set("status", status);
      if (search.trim()) params.set("search", search.trim());
      if (append && cursor) params.set("cursor", cursor);

      try {
        const page = await api.get<Page<Proposal>>(`/proposals?${params}`);
        setItems((prev) => (append && prev ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setError(null);
      } catch (err) {
        setError(err instanceof ApiFailure ? err.error.message : "Could not load proposals.");
      }
    },
    // `cursor` is deliberately excluded: including it would rebuild this
    // callback on every page load and re-trigger the effect below, paging
    // forever on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, search],
  );

  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  // A decision made in Discord should appear here without a refresh.
  useEffect(
    () =>
      subscribe(["proposal.submitted", "proposal.approved", "proposal.rejected"], () => {
        void load();
      }),
    [load],
  );

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Proposals</h1>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Every mass message submitted for review.
          </p>
        </div>
        <input
          className={`${inputClass} w-[260px]`}
          placeholder="Search message or model…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </header>

      <div className="flex gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={
              "rounded-[8px] px-3 py-1.5 text-[13px] transition-colors duration-150 " +
              (status === f.value
                ? "bg-[var(--brand-soft)] font-medium text-[var(--brand)]"
                : "text-[var(--muted)] hover:bg-[var(--surface-2)]")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {items === null ? (
        <div className="grid gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card padded={false}>
          <Empty
            title={status === "pending" ? "Nothing waiting" : "Nothing here"}
            body={
              status === "pending"
                ? "Every proposal has been decided. New ones appear the moment a chatter runs /mass in a model's channel."
                : "No proposals match this filter."
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => (
            <ProposalRow key={p.id} proposal={p} canDecide={canDecide} onDecided={() => void load()} />
          ))}
          {cursor && (
            <Button onClick={() => void load(true)} className="justify-self-center">
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ProposalRow({
  proposal,
  canDecide,
  onDecided,
}: {
  proposal: Proposal;
  canDecide: boolean;
  onDecided: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: "approved" | "rejected") {
    setBusy(true);
    setError(null);
    try {
      // No identity is sent. The decider is whoever this session belongs to,
      // and the server reads that from the cookie — a browser must never be
      // able to say who it is or what it is allowed to do.
      await api.post(`/proposals/${proposal.id}/console-decision`, {
        decision,
        reason: decision === "rejected" ? reason : null,
      });
      setRejecting(false);
      setReason("");
      onDecided();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "That did not go through.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold">{proposal.model}</span>
            <Badge tone={TONE[proposal.status]}>
              {proposal.status === "pending" ? "waiting" : proposal.status}
            </Badge>
            <span className="font-mono text-[11px] text-[var(--faint)]">#{proposal.id.slice(0, 8)}</span>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-[var(--foreground)]">
            {proposal.text}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--faint)]">
            <span>from {proposal.authorName}</span>
            <span>·</span>
            <span>{relativeTime(proposal.createdAt)}</span>
            {proposal.decidedByName && (
              <>
                <span>·</span>
                <span>
                  {proposal.status} by {proposal.decidedByName}
                </span>
              </>
            )}
          </div>
          {proposal.reason && (
            <div className="mt-2.5 rounded-[10px] bg-[var(--surface-2)] px-3 py-2.5">
              <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">
                Reason
              </div>
              <div className="mt-1 text-[13px] leading-relaxed text-[var(--muted)]">{proposal.reason}</div>
            </div>
          )}
        </div>

        {canDecide && proposal.status === "pending" && !rejecting && (
          <div className="flex shrink-0 gap-2">
            <Button variant="primary" onClick={() => void decide("approved")} disabled={busy}>
              Approve
            </Button>
            <Button variant="danger" onClick={() => setRejecting(true)} disabled={busy}>
              Reject
            </Button>
          </div>
        )}
      </div>

      {rejecting && (
        <div className="mt-4 grid gap-2 border-t border-[var(--border)] pt-4">
          <input
            className={inputClass}
            placeholder="Why? Optional — the chatter sees this."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="danger" onClick={() => void decide("rejected")} disabled={busy}>
              Confirm rejection
            </Button>
            <Button variant="ghost" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </Card>
  );
}
